import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Volume2, VolumeX, Sun, Moon, Users, Waves, TreePine, Mountain, Home, Flame, Fish, Clock, Play, Pause, RotateCcw, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// Timer durations in seconds
const TIMER_OPTIONS = [
  { label: '1 dk', seconds: 60 },
  { label: '3 dk', seconds: 180 },
  { label: '7 dk', seconds: 420 },
  { label: '15 dk', seconds: 900 }
];

// Environment configurations
const ENVIRONMENTS = [
  {
    id: 'forest',
    name: 'Orman',
    icon: TreePine,
    dayColors: ['#2d5a27', '#4a7c3f', '#6b8e23'],
    nightColors: ['#1a3318', '#2d4a28', '#1e3a1e'],
    particles: 'leaves',
    audioUrl: 'https://assets.mixkit.co/sfx/preview/mixkit-forest-birds-ambience-1210.mp3'
  },
  {
    id: 'beach',
    name: 'Deniz Kıyısı',
    icon: Waves,
    dayColors: ['#87ceeb', '#4682b4', '#f4a460'],
    nightColors: ['#1a3a4a', '#2c4a5a', '#3a4a5a'],
    particles: 'sand',
    audioUrl: 'https://assets.mixkit.co/sfx/preview/mixkit-sea-waves-loop-1196.mp3'
  },
  {
    id: 'rain',
    name: 'Yağmur',
    icon: Waves,
    dayColors: ['#708090', '#778899', '#696969'],
    nightColors: ['#2f3640', '#3d4554', '#4a5568'],
    particles: 'rain',
    audioUrl: 'https://assets.mixkit.co/sfx/preview/mixkit-light-rain-loop-2393.mp3'
  },
  {
    id: 'mountain',
    name: 'Dağ Zirvesi',
    icon: Mountain,
    dayColors: ['#87ceeb', '#b0c4de', '#dcdcdc'],
    nightColors: ['#1a1a2e', '#16213e', '#0f3460'],
    particles: 'mist',
    audioUrl: 'https://assets.mixkit.co/sfx/preview/mixkit-wind-in-the-trees-1234.mp3'
  },
  {
    id: 'room',
    name: 'Kapalı Oda',
    icon: Home,
    dayColors: ['#d4a574', '#c4956a', '#b08660'],
    nightColors: ['#2d2520', '#3d352f', '#4a4038'],
    particles: 'dust',
    audioUrl: 'https://assets.mixkit.co/sfx/preview/mixkit-hotel-lobby-with-murmur-and-music-453.mp3'
  },
  {
    id: 'fireplace',
    name: 'Şömine',
    icon: Flame,
    dayColors: ['#ff6b35', '#f7931e', '#ffd700'],
    nightColors: ['#8b4513', '#a0522d', '#cd853f'],
    particles: 'sparks',
    audioUrl: 'https://assets.mixkit.co/sfx/preview/mixkit-campfire-crackles-1330.mp3'
  },
  {
    id: 'underwater',
    name: 'Denizaltı',
    icon: Fish,
    dayColors: ['#006994', '#00b4d8', '#90e0ef'],
    nightColors: ['#003049', '#005577', '#006688'],
    particles: 'bubbles',
    audioUrl: 'https://assets.mixkit.co/sfx/preview/mixkit-underwater-ambient-2462.mp3'
  }
];

// Bell sound for timer completion
const playBellSound = () => {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  
  // Create bell-like sound with multiple harmonics
  const frequencies = [523.25, 659.25, 783.99]; // C5, E5, G5 chord
  
  frequencies.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.frequency.value = freq;
    osc.type = 'sine';
    
    // Bell envelope
    gain.gain.setValueAtTime(0.3 / (i + 1), ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 3);
    
    osc.start(ctx.currentTime + i * 0.1);
    osc.stop(ctx.currentTime + 3);
  });
  
  // Second bell after short pause
  setTimeout(() => {
    frequencies.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.value = freq * 1.5; // Higher octave
      osc.type = 'sine';
      
      gain.gain.setValueAtTime(0.2 / (i + 1), ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 2);
      
      osc.start(ctx.currentTime + i * 0.1);
      osc.stop(ctx.currentTime + 2);
    });
  }, 1500);
};

// Particle System Component
const ParticleSystem = ({ type, isDarkMode }) => {
  const particles = [];
  const count = type === 'rain' ? 50 : 20;
  
  for (let i = 0; i < count; i++) {
    const style = {
      left: `${Math.random() * 100}%`,
      animationDelay: `${Math.random() * 5}s`,
      animationDuration: `${3 + Math.random() * 4}s`
    };
    
    let particleClass = 'absolute rounded-full opacity-60';
    
    switch (type) {
      case 'leaves':
        particleClass += ' w-2 h-2 bg-green-400/40';
        break;
      case 'sand':
        particleClass += ' w-1 h-1 bg-amber-300/30';
        break;
      case 'rain':
        particleClass += ' w-0.5 h-4 bg-blue-300/50';
        break;
      case 'mist':
        particleClass += ' w-20 h-20 bg-white/10 blur-xl';
        break;
      case 'dust':
        particleClass += ' w-1 h-1 bg-amber-200/20';
        break;
      case 'sparks':
        particleClass += ' w-1 h-1 bg-orange-400/60';
        break;
      case 'bubbles':
        particleClass += ' w-3 h-3 bg-cyan-300/30 rounded-full';
        break;
      default:
        particleClass += ' w-1 h-1 bg-white/30';
    }
    
    particles.push(
      <motion.div
        key={i}
        className={particleClass}
        style={style}
        animate={{
          y: type === 'bubbles' ? [window.innerHeight, -50] : [-50, window.innerHeight],
          x: type === 'leaves' ? [0, Math.random() * 100 - 50] : 0,
          opacity: [0.3, 0.7, 0.3],
          scale: type === 'mist' ? [1, 1.5, 1] : 1
        }}
        transition={{
          duration: type === 'rain' ? 1 : 5 + Math.random() * 5,
          repeat: Infinity,
          ease: 'linear',
          delay: Math.random() * 5
        }}
      />
    );
  }
  
  return <div className="absolute inset-0 overflow-hidden pointer-events-none">{particles}</div>;
};

// Environment Background
const EnvironmentBackground = ({ environment, isDarkMode }) => {
  const colors = isDarkMode ? environment.nightColors : environment.dayColors;
  
  return (
    <div className="absolute inset-0 transition-colors duration-1000">
      <div 
        className="absolute inset-0 transition-all duration-1000"
        style={{
          background: `linear-gradient(180deg, ${colors[0]} 0%, ${colors[1]} 50%, ${colors[2]} 100%)`
        }}
      />
      
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-1/3 opacity-30"
        style={{
          background: `linear-gradient(0deg, ${colors[2]} 0%, transparent 100%)`
        }}
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />
      
      <ParticleSystem type={environment.particles} isDarkMode={isDarkMode} />
      
      <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/30" />
    </div>
  );
};

// Timer Display Component
const TimerDisplay = ({ seconds, isRunning, onToggle, onReset, selectedDuration }) => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const progress = selectedDuration > 0 ? ((selectedDuration - seconds) / selectedDuration) * 100 : 0;
  
  return (
    <div className="relative">
      {/* Circular progress */}
      <svg className="w-32 h-32 transform -rotate-90">
        <circle
          cx="64"
          cy="64"
          r="58"
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="4"
        />
        <motion.circle
          cx="64"
          cy="64"
          r="58"
          fill="none"
          stroke="url(#timerGradient)"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={364}
          strokeDashoffset={364 - (364 * progress) / 100}
          initial={{ strokeDashoffset: 364 }}
          animate={{ strokeDashoffset: 364 - (364 * progress) / 100 }}
          transition={{ duration: 0.5 }}
        />
        <defs>
          <linearGradient id="timerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#a78bfa" />
          </linearGradient>
        </defs>
      </svg>
      
      {/* Time display */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-white/90">
          {String(minutes).padStart(2, '0')}:{String(secs).padStart(2, '0')}
        </span>
      </div>
    </div>
  );
};

// Timer Controls Component
const TimerControls = ({ 
  selectedDuration, 
  setSelectedDuration, 
  isRunning, 
  onStart, 
  onPause, 
  onReset,
  timeLeft 
}) => {
  return (
    <div className="space-y-4">
      {/* Duration selection */}
      {!isRunning && timeLeft === selectedDuration && (
        <div className="flex justify-center gap-2">
          {TIMER_OPTIONS.map((option) => (
            <button
              key={option.seconds}
              onClick={() => setSelectedDuration(option.seconds)}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                selectedDuration === option.seconds
                  ? 'bg-violet-500/30 text-violet-300 border border-violet-500/50'
                  : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
      
      {/* Play/Pause/Reset buttons */}
      <div className="flex justify-center gap-3">
        {isRunning ? (
          <button
            onClick={onPause}
            className="p-4 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all"
          >
            <Pause className="w-6 h-6" />
          </button>
        ) : (
          <button
            onClick={onStart}
            className="p-4 rounded-full bg-gradient-to-r from-violet-500/30 to-cyan-500/30 text-white hover:from-violet-500/40 hover:to-cyan-500/40 transition-all border border-violet-500/30"
          >
            <Play className="w-6 h-6" />
          </button>
        )}
        
        {(timeLeft < selectedDuration || !isRunning) && timeLeft !== selectedDuration && (
          <button
            onClick={onReset}
            className="p-4 rounded-full bg-white/10 text-white/60 hover:bg-white/20 hover:text-white transition-all"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

// Main Meditation Room Component
export const MeditationRoom = ({ onBack, onHome }) => {
  const [selectedEnv, setSelectedEnv] = useState(ENVIRONMENTS[0]);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [userCount, setUserCount] = useState(1);
  const [showEnvSelector, setShowEnvSelector] = useState(false);
  
  // Timer state
  const [selectedDuration, setSelectedDuration] = useState(180); // Default 3 minutes
  const [timeLeft, setTimeLeft] = useState(180);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerCompleted, setTimerCompleted] = useState(false);
  
  const audioRef = useRef(null);
  const wsRef = useRef(null);
  const timerRef = useRef(null);
  
  // Timer effect
  useEffect(() => {
    if (isTimerRunning && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsTimerRunning(false);
            setTimerCompleted(true);
            playBellSound();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isTimerRunning, timeLeft]);
  
  // Reset timer when duration changes
  useEffect(() => {
    setTimeLeft(selectedDuration);
    setTimerCompleted(false);
  }, [selectedDuration]);
  
  // WebSocket for live user count
  useEffect(() => {
    const connectWs = () => {
      try {
        const ws = new WebSocket(`${WS_URL}/api/meditation/ws`);
        
        ws.onmessage = (event) => {
          const data = JSON.parse(event.data);
          if (data.type === 'user_count') {
            setUserCount(data.count);
          }
        };
        
        ws.onopen = () => {
          ws.send(JSON.stringify({ action: 'join' }));
        };
        
        wsRef.current = ws;
      } catch (e) {
        setUserCount(Math.floor(Math.random() * 5) + 1);
      }
    };
    
    connectWs();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);
  
  // Audio management
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    const audio = new Audio(selectedEnv.audioUrl);
    audio.loop = true;
    audio.volume = 0.3;
    audioRef.current = audio;
    
    if (!isMuted) {
      audio.play().catch(() => {});
    }
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, [selectedEnv]);
  
  useEffect(() => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(() => {});
      }
    }
  }, [isMuted]);
  
  const handleEnvSelect = (env) => {
    setSelectedEnv(env);
    setShowEnvSelector(false);
  };
  
  const handleTimerStart = () => {
    setIsTimerRunning(true);
    setTimerCompleted(false);
  };
  
  const handleTimerPause = () => {
    setIsTimerRunning(false);
  };
  
  const handleTimerReset = () => {
    setIsTimerRunning(false);
    setTimeLeft(selectedDuration);
    setTimerCompleted(false);
  };
  
  const Icon = selectedEnv.icon;
  
  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="meditation-room">
      {/* Environment Background */}
      <EnvironmentBackground environment={selectedEnv} isDarkMode={isDarkMode} />
      
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-40 p-4 flex items-center justify-between">
        <button
          onClick={onBack}
          className="glass-panel p-3 text-slate-300 hover:text-white transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        
        <div className="flex items-center gap-3">
          {/* User count */}
          <div className="glass-panel px-4 py-2 flex items-center gap-2">
            <Users className="w-4 h-4 text-violet-400" />
            <span className="text-sm text-slate-300">{userCount}</span>
          </div>
          
          {/* Day/Night toggle */}
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className="glass-panel p-3 text-slate-300 hover:text-white transition-all"
          >
            {isDarkMode ? <Moon className="w-5 h-5 text-blue-400" /> : <Sun className="w-5 h-5 text-amber-400" />}
          </button>
          
          {/* Sound toggle */}
          <button
            onClick={() => setIsMuted(!isMuted)}
            className="glass-panel p-3 text-slate-300 hover:text-white transition-all"
          >
            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5 text-cyan-400" />}
          </button>
          
          {/* Home Button */}
          <button
            onClick={onHome || onBack}
            className="glass-panel p-3 text-slate-300 hover:text-white transition-all flex items-center gap-2"
            data-testid="home-button"
          >
            <Home className="w-5 h-5" />
            <span className="text-sm">Home</span>
          </button>
        </div>
      </div>
      
      {/* Center Content */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          className="text-center space-y-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {/* Title */}
          <div className="space-y-2">
            <h1 className="text-3xl font-heading text-white/90">Meditasyon Odası</h1>
            <p className="text-white/60 text-sm">{selectedEnv.name} ortamında odaklanın</p>
          </div>
          
          {/* Timer */}
          <div className="space-y-6">
            <TimerDisplay 
              seconds={timeLeft} 
              isRunning={isTimerRunning}
              selectedDuration={selectedDuration}
            />
            
            <TimerControls
              selectedDuration={selectedDuration}
              setSelectedDuration={setSelectedDuration}
              isRunning={isTimerRunning}
              onStart={handleTimerStart}
              onPause={handleTimerPause}
              onReset={handleTimerReset}
              timeLeft={timeLeft}
            />
          </div>
          
          {/* Timer completed message */}
          <AnimatePresence>
            {timerCompleted && (
              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                <p className="text-cyan-400 text-lg font-heading">Meditasyon Tamamlandı</p>
                <p className="text-white/50 text-sm">Nazikçe gözlerinizi açın</p>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* User count */}
          <motion.p
            className="text-white/40 text-xs"
            animate={{ opacity: [0.4, 0.7, 0.4] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            Şu anda odaklanan kişi sayısı: {userCount}
          </motion.p>
        </motion.div>
      </div>
      
      {/* Bottom Bar with Environment Selector and Exit Button */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3">
        <button
          onClick={() => setShowEnvSelector(true)}
          className="glass-panel px-6 py-3 text-slate-300 hover:text-white transition-all flex items-center gap-2"
        >
          <Icon className="w-4 h-4" />
          <span className="text-sm">{selectedEnv.name}</span>
          <span className="text-slate-500 text-xs">• Ortam Değiştir</span>
        </button>
        
        <button
          onClick={onBack}
          className="glass-panel px-6 py-3 text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all flex items-center gap-2"
          data-testid="meditation-exit-btn"
        >
          <X className="w-4 h-4" />
          <span className="text-sm">Bitir</span>
        </button>
      </div>
      
      {/* Environment Selector Modal */}
      <AnimatePresence>
        {showEnvSelector && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowEnvSelector(false)}
          >
            <motion.div
              className="glass-panel p-6 max-w-md w-full space-y-4"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-heading text-slate-100 text-center">Ortam Seçin</h3>
              
              <div className="grid grid-cols-2 gap-3">
                {ENVIRONMENTS.map((env) => {
                  const EnvIcon = env.icon;
                  const isSelected = selectedEnv.id === env.id;
                  
                  return (
                    <button
                      key={env.id}
                      onClick={() => handleEnvSelect(env)}
                      className={`p-4 rounded-2xl border transition-all flex flex-col items-center gap-2 ${
                        isSelected
                          ? 'bg-violet-500/20 border-violet-500/50'
                          : 'bg-slate-800/50 border-slate-700/50 hover:border-violet-500/30'
                      }`}
                    >
                      <EnvIcon className={`w-6 h-6 ${isSelected ? 'text-violet-400' : 'text-slate-400'}`} />
                      <span className={`text-sm ${isSelected ? 'text-violet-300' : 'text-slate-300'}`}>
                        {env.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MeditationRoom;
